import React from 'react';
import '../../../pages/dashboard/Dashboard.css';

export interface SidebarMenuItem {
  id: string;
  label: string;
  /** Emoji or character when iconElement is not provided (e.g. for other pages) */
  icon: string;
  /** Optional React node for the icon (e.g. Lucide icon). When set, used instead of icon. */
  iconElement?: React.ReactNode;
  onClick: () => void;
  isActive?: boolean;
  isLogout?: boolean;
  /** Render as a card at the top (e.g. View Profile) – single line, no subtitle */
  isProfileCard?: boolean;
  /** Optional count badge (e.g. notifications). Omit to show no badge. */
  badgeCount?: number;
  /** Show a separator line above this item */
  separatorBefore?: boolean;
  /** Optional nested items (e.g. Patient name → Meal plan sections under Patients) */
  children?: SidebarMenuItem[];
}

interface DashboardSidebarProps {
  items: SidebarMenuItem[];
  className?: string;
}

/** Renders a single nav button (no children). */
function renderNavButton(item: SidebarMenuItem, nested = false) {
  const className = `dashboard-nav-item ${nested ? 'dashboard-nav-item-nested' : ''} ${item.isActive ? 'active' : ''}`;
  return (
    <button key={item.id} type="button" className={className} onClick={item.onClick}>
      {!nested && <span className="nav-icon">{item.iconElement ?? item.icon}</span>}
      {nested && <span className="nav-icon nav-icon-spacer" aria-hidden />}
      <span>{item.label}</span>
      {item.badgeCount != null && item.badgeCount > 0 && (
        <span className="dashboard-nav-badge">{item.badgeCount}</span>
      )}
    </button>
  );
}

/** Renders an item and its children recursively so all nesting levels are visible. */
function renderNavItemWithChildren(item: SidebarMenuItem, nested: boolean) {
  return (
    <React.Fragment key={item.id}>
      {renderNavButton(item, nested)}
      {item.children && item.children.length > 0 && (
        <div className="dashboard-nav-children">
          {item.children.map((child) => renderNavItemWithChildren(child, true))}
        </div>
      )}
    </React.Fragment>
  );
}

export const DashboardSidebar: React.FC<DashboardSidebarProps> = ({
  items,
  className = '',
}) => {
  const profileItem = items.find((i) => i.isProfileCard);
  const logoutItem = items.find((i) => i.isLogout);
  const middleItems = items.filter((i) => !i.isProfileCard && !i.isLogout);

  return (
    <aside className={`dashboard-sidebar ${className}`}>
      <nav className="dashboard-nav">
        {profileItem && (
          <button
            key={profileItem.id}
            type="button"
            className={`dashboard-nav-item ${profileItem.isActive ? 'active' : ''}`}
            onClick={profileItem.onClick}
          >
            <span className="nav-icon">{profileItem.iconElement ?? profileItem.icon}</span>
            <span>{profileItem.label}</span>
          </button>
        )}
        {middleItems.map((item) => (
          <React.Fragment key={item.id}>
            {item.separatorBefore && <div className="dashboard-nav-separator" />}
            {item.children && item.children.length > 0 ? (
              <div className="dashboard-nav-group">
                <button
                  type="button"
                  className={`dashboard-nav-item ${item.isActive ? 'active' : ''}`}
                  onClick={item.onClick}
                >
                  <span className="nav-icon">{item.iconElement ?? item.icon}</span>
                  <span>{item.label}</span>
                  {item.badgeCount != null && item.badgeCount > 0 && (
                    <span className="dashboard-nav-badge">{item.badgeCount}</span>
                  )}
                </button>
                <div className="dashboard-nav-children">
                  {item.children.map((child) => renderNavItemWithChildren(child, true))}
                </div>
              </div>
            ) : (
              <button
                type="button"
                className={`dashboard-nav-item ${item.isActive ? 'active' : ''}`}
                onClick={item.onClick}
              >
                <span className="nav-icon">{item.iconElement ?? item.icon}</span>
                <span>{item.label}</span>
                {item.badgeCount != null && item.badgeCount > 0 && (
                  <span className="dashboard-nav-badge">{item.badgeCount}</span>
                )}
              </button>
            )}
          </React.Fragment>
        ))}
        {logoutItem && (
          <button
            key={logoutItem.id}
            type="button"
            className={`dashboard-nav-item ${logoutItem.isActive ? 'active' : ''}`}
            onClick={logoutItem.onClick}
          >
            <span className="nav-icon">{logoutItem.iconElement ?? logoutItem.icon}</span>
            <span>{logoutItem.label}</span>
          </button>
        )}
      </nav>
    </aside>
  );
};

